import { useRef, useEffect } from "react";
import { Box, Button, MenuItem, Pagination, Select, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getUserType } from "util/auth";
import MenuPriceSetting from "components/menu/MenuPriceSetting";
import SearchItem from "components/SearchItem";
import TableTireBrand from "../components/TableTireBrand";
import useTireBrandDetail from "../hooks/useTireBrandDetail";
import { BrandDetailTireUpdateRequest } from "../types/tire";

/**
 * The TireBrand Page.
 */
function TireBrand() {
  const navigate = useNavigate();
  const userType = getUserType();
  const {
    tireBrandDetail,
    setTireBrandDetail,
    updateTireBrandDetail,
    loading,
    handlePageChange,
    setErrors,
    errors,
    categoryOption,
    queryParams,
    setQueryParams,
    totalPage,
    currentPage,
    searchValue,
    setSearchValue,
  } = useTireBrandDetail();

  const tableContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Handle page change and scroll table to top
   */
  const handlePageChangeWithScroll = (event: React.ChangeEvent<unknown>, page: number) => {
    handlePageChange(event, page);
    // Scroll table container to top when page changes
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  };

  /**
   * Scroll table container to top when filters change
   */
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [queryParams.item_category_m, queryParams.maker_or_brand]);

  const onChangeItem = (index: number, key: string, value: string) => {
    setTireBrandDetail((prev) => {
      const newTireBrandDetail = [...prev];
      newTireBrandDetail[index] = {
        ...newTireBrandDetail[index],
        [key]: value,
      };
      return newTireBrandDetail;
    });
  };

  const handleUpdate = (index: number, key: keyof BrandDetailTireUpdateRequest) => {
    if (errors && errors[index] && errors[index][key]) return;
    updateTireBrandDetail(index, key);
  };

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate("/")} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box
        sx={{ display: "flex", gap: 3, flexWrap: "wrap", p: { xs: 2, md: 2.5, lg: 5 } }}
      >
      <Box
        sx={{
          display: { xs: "flex", xl: "block" },
          justifyContent: "space-between",
          flexBasis: { xs: "100%", xl: "290px" },
        }}
      >
        <Box sx={{ marginBottom: "50px" }}>
          <Typography
            margin={0}
            sx={{
              fontSize: { xs: 24, md: 28, lg: 33 },
              color: "var(--gray-500)",
              fontWeight: "medium",
            }}
          >
            {userType === "C0002" ? "店舗別" : "マスタ管理"}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 20, md: 28 },
              color: "var(--gray-500)",
              fontWeight: "medium",
            }}
          >
            {userType === "C0002" ? "各種設定" : "売価設定"}
            {userType !== "C0002" && (
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: 20, md: 28 },
                  color: "var(--gray-500)",
                  fontWeight: "medium",
                }}
              >
                <br /> タイヤ
              </Typography>
            )}
          </Typography>
        </Box>

        <MenuPriceSetting />
      </Box>
      <Box sx={{ flex: 1, width: { xs: "100%", lg: "auto" } }}>
        <Box
          sx={{
            maxWidth: "1208px",
            width: "100%",
          }}
        >
          <Typography
            margin={0}
            sx={{
              fontSize: { xs: 18, md: 20, lg: 25 },
              color: "var(--gray-500)",
              fontWeight: "medium",
            }}
          >
            タイヤ売価設定
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 24, md: 28, lg: 35 },
              color: "var(--black)",
            }}
          >
            ブランド詳細を設定します。
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            mt: { xs: 3, lg: 7 },
            mb: 1,
            width: "100%",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: { xs: "100%", md: "auto" },
                gap: 2,
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}>中分類</Typography>
              <Select
                sx={{
                  maxWidth: 300,
                  width: { xs: 280, xl: 300 },
                  height: { xs: 32, lg: 48 },
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  color: "var(--gray-700)",
                  "& .MuiSelect-select": {
                    padding: "0 16px",
                    paddingRight: "32px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
                value={queryParams.item_category_m}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span>全て</span>;
                  }
                  const selectedOption = categoryOption.find((opt) => opt.value === selected);
                  const displayText = selectedOption?.label || selected;
                  return (
                    <Box
                      title={displayText}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      {displayText}
                    </Box>
                  );
                }}
                onChange={(event) =>
                  setQueryParams((prev) => ({
                    ...prev,
                    item_category_m: event.target.value,
                    offset: 1,
                  }))
                }
              >
                <MenuItem value="">全て</MenuItem>
                {categoryOption.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <SearchItem
              label="メーカー名/ブランド名"
              value={searchValue}
              type="input"
              onChange={(value) => setSearchValue(value as string)}
              containerSx={{ maxWidth: 300 }}
              labelSx={{ height: { xs: 32, lg: 48 }, minHeight: { xs: 32, lg: 48 }, px: 1, minWidth: "auto" }}
              valueSx={{ "& .MuiOutlinedInput-root": { height: { xs: 32, lg: 48 }, width: "100%" } }}
            />
          </Box>

          {userType === "C0001" ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'end', width: { xs: '100%', lg: 'auto' } }}>
              <Typography
                variant="h6"
                sx={{
                  color: "var(--black)",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  fontWeight: "bold",
                  textAlign: 'right',
                }}
              >
                通常価格＝A表価格×A表売価掛率
              </Typography>
              <Typography sx={{ fontSize: 12, textAlign: 'right' }}>
                位置の変更 画像の位置に変更してほしいです。
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "var(--black)",
                  fontSize: 12,
                  fontWeight: "bold",
                  textAlign: 'right',
                }}
              >
                ※1 A表売価掛け率が「0」の場合は、定価を参照
                <br />
                ※2 ホイールセット時のタイヤ粗利率
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "var(--black)",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  fontWeight: "bold",
                  marginRight: "20px",
                }}
              >
                黒字：本部設定
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "var(--red-800)",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  fontWeight: "bold",
                  marginRight: "15px",
                }}
              >
                赤字：店舗上書設定
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          ref={tableContainerRef}
          sx={{
            overflowX: "auto",
            overflowY: "auto",
            width: "100%",
            maxHeight: { xs: "calc(100vh - 250px)", lg: "calc(100vh - 200px)" },
          }}
        >
          <TableTireBrand
            tireBrandList={tireBrandDetail}
            onChangeItem={(index, key, value) => onChangeItem(index, key, value)}
            onUpdateItem={(index, key) => handleUpdate(index, key)}
            setErrors={setErrors}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPage}
            page={currentPage}
            onChange={handlePageChangeWithScroll}
            color="primary"
            disabled={loading}
          />
        </Box>
      </Box>
    </Box>
    </>
  );
}

export default TireBrand;
